import { Plus, Check, Settings2, Globe, SlidersHorizontal, Wallet } from 'lucide-react'
import AssetCard from '@/components/AssetCard'
//import SearchBar from '@/components/SearchBar'
import { useBalance } from '@/hooks/useBalance'
import Skeleton from '@/components/common/Skeleton'
import { useCurrency } from '@/hooks/useCurrency'
import AddAssetDialog from '@/components/dialogs/AddAssetDialog'
import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { getNetwork, getAllNetworks } from '@/config/networks'

const Assets = () => {
  const { t } = useTranslation()
  const {
    allNetworksTokens,
    allNetworksLoaded,
    refreshBalance,
    refreshAllNetworksBalance
  } = useBalance()
  const { formatValue } = useCurrency()
  const [openCustomAssetDialog, setOpenCustomAssetDialog] = useState(false)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [hideZeroBalance, setHideZeroBalance] = useState(false)
  const filterRef = useRef<HTMLDivElement>(null)
  const [isNetworkFilterOpen, setIsNetworkFilterOpen] = useState(false)
  const [networkFilter, setNetworkFilter] = useState<string>('all')
  const networkFilterRef = useRef<HTMLDivElement>(null)
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false)
  const mobileFilterRef = useRef<HTMLDivElement>(null)

  const availableNetworks = getAllNetworks().filter(
    (network) => network.id === 'omdb' || network.id === 'bsc' || network.isCustom
  )
  const activeNetworkFilter = availableNetworks.find((n) => n.id === networkFilter)

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target as Node)
      ) {
        setIsFilterOpen(false)
      }
      if (
        networkFilterRef.current &&
        !networkFilterRef.current.contains(event.target as Node)
      ) {
        setIsNetworkFilterOpen(false)
      }
      if (
        mobileFilterRef.current &&
        !mobileFilterRef.current.contains(event.target as Node)
      ) {
        setIsMobileFilterOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const filteredTokens = allNetworksTokens.filter((token) => {
    if (networkFilter !== 'all' && token.network !== networkFilter) {
      return false
    }
    if (hideZeroBalance) {
      return parseFloat(token.balance || '0') > 0
    }
    return true
  })

  // allNetworksLoaded se pone en true apenas hay un total cacheado, pero allNetworksTokens
  // no se cachea y tarda un momento más — sin este chequeo se veía "no tienes activos" un
  // instante antes de que lleguen los tokens reales (mismo caso que en Dashboard.tsx)
  const assetsLoading = !allNetworksLoaded || allNetworksTokens.length === 0

  const renderAssetCards = (layout: 'grid' | 'row' = 'grid') => {
    if (assetsLoading) {
      return [1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
        <Skeleton
          key={i}
          className={
            layout === 'grid'
              ? 'h-48 w-full rounded-3xl'
              : 'h-24 w-full rounded-3xl'
          }
        />
      ))
    }

    if (filteredTokens.length === 0) {
      return (
        <div className="col-span-full py-20 text-center border-2 border-dashed border-text-main/10 rounded-3xl">
          <p className="text-text-secondary">{t('assets.no_assets')}</p>
        </div>
      )
    }

    return filteredTokens.map((token, index) => {
      const network = getNetwork(token.network || '')
      return (
        <AssetCard
          key={token.network + token.address + token.symbol}
          logoUrl={token.logoUrl}
          icon={<Wallet className="icon-gradient w-6 h-6" />}
          name={token.name}
          code={token.symbol}
          value={parseFloat(token.balanceFormatted || '0')}
          change={`${(token.priceChange24h || 0) > 0 ? '+' : ''}${(token.priceChange24h || 0).toFixed(2)}%`}
          isPositive={(token.priceChange24h || 0) >= 0}
          borderColor={
            index % 3 === 0
              ? 'border-primary'
              : index % 3 === 1
                ? 'border-accent'
                : 'border-tertiary'
          }
          usdValue={`${formatValue(parseFloat(token.balance || '0') * (token.priceUSD || 0))}`}
          layout={layout}
          network={token.network}
          networkName={network?.name}
          networkLogoUrl={network?.logoUrl}
        />
      )
    })
  }

  return (
    <>
      {/* MOBILE VIEW ONLY */}
      <div className="flex flex-col gap-4 mt-4 lg:hidden">
        <div className="flex justify-between items-center gap-3">
          <button
            onClick={() => setOpenCustomAssetDialog(true)}
            className="btn-gradient-fill flex gap-2 rounded-full py-4 px-6 max-w-max"
          >
            <Plus className="w-4 h-4" />
            <p className="font-extrabold text-sm uppercase">
              {t('assets.add_asset')}
            </p>
          </button>

          <div className="relative" ref={mobileFilterRef}>
            <button
              onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)}
              className="flex gap-2 items-center bg-card text-text-secondary shadow-sm rounded-full py-3 px-5 border border-border-base dark:border-0"
            >
              <SlidersHorizontal className="w-4 h-4" />
              <p className="text-sm font-semibold">{t('assets.filters')}</p>
            </button>

            {isMobileFilterOpen && (
              <div className="absolute top-full mt-2 right-0 w-64 rounded-xl bg-card border border-border-base shadow-xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="p-2 space-y-1">
                  <p className="px-3 py-1.5 text-xs font-semibold text-text-secondary uppercase tracking-wider">
                    {t('assets.network')}
                  </p>
                  <button
                    onClick={() => setNetworkFilter('all')}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                      networkFilter === 'all'
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-text-main hover:bg-card-hover'
                    }`}
                  >
                    {t('assets.all_networks')}
                    {networkFilter === 'all' && <Check className="w-4 h-4" />}
                  </button>
                  {availableNetworks.map((network) => (
                    <button
                      key={network.id}
                      onClick={() => setNetworkFilter(network.id)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                        networkFilter === network.id
                          ? 'bg-primary/10 text-primary font-medium'
                          : 'text-text-main hover:bg-card-hover'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full overflow-hidden bg-primary/20 flex items-center justify-center shrink-0">
                          {network.logoUrl ? (
                            <img
                              src={network.logoUrl}
                              alt={network.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Globe className="w-3.5 h-3.5 text-primary" />
                          )}
                        </div>
                        <span>{network.name}</span>
                      </div>
                      {networkFilter === network.id && (
                        <Check className="w-4 h-4" />
                      )}
                    </button>
                  ))}
                  <div className="border-t border-border-base my-1" />
                  <button
                    onClick={() => setHideZeroBalance(!hideZeroBalance)}
                    className="w-full flex gap-3 items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors text-text-main hover:bg-card-hover"
                  >
                    {t('assets.hide_zero_balance')}
                    {hideZeroBalance && (
                      <Check className="w-4 h-4 text-primary" />
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        {renderAssetCards('row')}
      </div>

      {/* DESKTOP VIEW ONLY */}
      <div className="gap-8 hidden lg:flex lg:flex-col">
        <div className="flex justify-between items-center mb-4">
          <div className="flex flex-col gap-2">
            <h2 className="font-extrabold text-4xl text-text-main">
              {t('assets.title')}
            </h2>
            <p className="font-medium text-text-secondary">
              {t('assets.desc')}
            </p>
          </div>

          <button
            onClick={() => setOpenCustomAssetDialog(true)}
            className="btn-gradient-fill flex gap-2 rounded-full h-14 py-4 px-6 max-w-max"
          >
            <Plus className="w-4 h-4" />
            <p className="font-extrabold text-sm">
              {t('assets.add_custom_asset')}
            </p>
          </button>
        </div>

        <div className="flex gap-3 self-end">
          {/* <SearchBar placeholder={t('assets.search_placeholder')} /> */}
          <div className="relative" ref={networkFilterRef}>
            <button
              onClick={() => setIsNetworkFilterOpen(!isNetworkFilterOpen)}
              className="flex gap-2 items-center bg-card text-text-secondary max-w-min shadow-sm rounded-xl py-3 px-6 border border-border-base dark:border-0 hover:bg-card-hover transition-colors"
            >
              <div className="w-4 h-4 rounded-full overflow-hidden bg-primary/20 flex items-center justify-center shrink-0">
                {activeNetworkFilter?.logoUrl ? (
                  <img
                    src={activeNetworkFilter.logoUrl}
                    alt={activeNetworkFilter.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Globe className="w-2.5 h-2.5 text-primary" />
                )}
              </div>
              <p className="whitespace-nowrap">
                {activeNetworkFilter?.name || t('assets.all_networks')}
              </p>
            </button>

            {isNetworkFilterOpen && (
              <div className="absolute top-full mt-2 right-0 w-56 rounded-xl bg-card border border-border-base shadow-xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="p-2 space-y-1">
                  <button
                    onClick={() => {
                      setNetworkFilter('all')
                      setIsNetworkFilterOpen(false)
                    }}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                      networkFilter === 'all'
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-text-main hover:bg-card-hover'
                    }`}
                  >
                    {t('assets.all_networks')}
                    {networkFilter === 'all' && <Check className="w-4 h-4" />}
                  </button>
                  {availableNetworks.map((network) => (
                    <button
                      key={network.id}
                      onClick={() => {
                        setNetworkFilter(network.id)
                        setIsNetworkFilterOpen(false)
                      }}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                        networkFilter === network.id
                          ? 'bg-primary/10 text-primary font-medium'
                          : 'text-text-main hover:bg-card-hover'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full overflow-hidden bg-primary/20 flex items-center justify-center shrink-0">
                          {network.logoUrl ? (
                            <img
                              src={network.logoUrl}
                              alt={network.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Globe className="w-3.5 h-3.5 text-primary" />
                          )}
                        </div>
                        <span>{network.name}</span>
                      </div>
                      {networkFilter === network.id && (
                        <Check className="w-4 h-4" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="relative" ref={filterRef}>
            <button
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="flex gap-2 items-center bg-card text-text-secondary max-w-min shadow-sm rounded-xl py-3 px-6 border border-border-base dark:border-0 hover:bg-card-hover transition-colors"
            >
              <Settings2 className="w-4 h-4" />
              <p>{t('assets.options')}</p>
            </button>

            {isFilterOpen && (
              <div className="absolute top-full mt-2 right-0 w-max rounded-xl bg-card border border-border-base shadow-xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="p-2 space-y-1">
                  <button
                    onClick={() => setHideZeroBalance(!hideZeroBalance)}
                    className="w-full flex gap-3 items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors text-text-main hover:bg-card-hover"
                  >
                    {t('assets.hide_zero_balance')}
                    {hideZeroBalance && (
                      <Check className="w-4 h-4 text-primary" />
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
          {renderAssetCards('grid')}
        </div>
      </div>
      <AddAssetDialog
        open={openCustomAssetDialog}
        onClose={() => setOpenCustomAssetDialog(false)}
        onTokenAdded={() => {
          refreshBalance(false)
          refreshAllNetworksBalance()
        }}
      />
    </>
  )
}

export default Assets
